/**
 * CollapsibleMarkdownList — the `ul` / `ol` renderer used by SkillResultMarkdown.
 *
 * WHY. The prose renderer had no list bound at all, so an answer that enumerated
 * "every class that touches Opportunity" rendered 400 bullets inline: the reader
 * lost the answer's structure, the follow-up controls below the report were pushed
 * off-screen, and scrolling a past turn back into view meant paging through a list
 * nobody asked to read in full. Code fences are already collapsed elsewhere; lists
 * were the remaining unbounded block.
 *
 * THE RULE. A list longer than `LIST_COLLAPSE_THRESHOLD` renders its first N items
 * plus a toggle; everything shorter renders exactly as before, with no toggle and no
 * wrapper semantics changed. The toggle is a real <button> (keyboard-reachable,
 * `aria-expanded`, `aria-controls` pointing at the list it governs) rather than a
 * <details>, because the collapsed state must keep the first N items VISIBLE — a
 * <details> hides the whole list.
 *
 * WHY IT DOES NOT COLLAPSE WHILE STREAMING. During generation the list grows from
 * the bottom. Collapsing at item 31 would hide precisely the items being written,
 * so the user would watch the answer stop moving while it was still moving. The
 * collapse engages once the turn stops streaming, which is also when the list has a
 * final length worth reporting in the toggle label.
 *
 * WHY `start` IS PASSED THROUGH. An `<ol start="7">` that collapses must still begin
 * at 7. Slicing from the FRONT keeps the browser's own numbering correct for both
 * states — which is the other reason the collapsed view is a head slice, not a tail
 * slice like the evidence panel's.
 *
 * STYLING. Deliberately theme-token Tailwind (`text-muted-foreground`) rather than
 * the multi-agent `--ma-*` custom properties the evidence-list toggle in
 * ResultColumn uses: this renderer is shared with the studio and dev-agent chat
 * surfaces, where those variables are not defined and the label would inherit an
 * arbitrary color. The label wording mirrors ResultColumn's so the two collapses
 * read as one idiom.
 */
import { Children, isValidElement, useId, useMemo, useState, type ReactNode } from "react";
import { cn } from "@/lib/studio/utils";

/**
 * Streamdown's own `ul`/`ol` classes and `data-streamdown` markers, reproduced
 * because a `components` override REPLACES the default renderer rather than wrapping
 * it (`{...defaults, ...userComponents}` in streamdown's Markdown block). Dropping
 * them would silently strip every bullet and number from every list in the app — the
 * kind of regression that looks like a CSS bug three files away.
 */
const UL_CLASS = "list-inside list-disc whitespace-normal [li_&]:pl-6";
const OL_CLASS = "list-inside list-decimal whitespace-normal [li_&]:pl-6";

/** Lists longer than this collapse. Chosen to sit above ordinary prose lists (a
 *  step list, a five-point summary) and below the machine-generated inventories
 *  that motivated this — it must never fire on a list a human would have written. */
export const LIST_COLLAPSE_THRESHOLD = 30;

/**
 * Whether a list of `itemCount` items should render collapsed. Pure so the rule
 * can be asserted without a DOM: the render below is only the consequence.
 */
export function shouldCollapseList(itemCount: number, isStreaming: boolean): boolean {
  if (isStreaming) return false;
  return itemCount > LIST_COLLAPSE_THRESHOLD;
}

/**
 * The renderable children of a `ul`/`ol` — markdown puts a "\n" text node between
 * every `<li>`, and counting those as items would collapse a 16-item list.
 */
export function listItems(children: ReactNode): ReactNode[] {
  return Children.toArray(children).filter((c) => isValidElement(c));
}

export interface CollapsibleMarkdownListProps {
  ordered?: boolean;
  /** `<ol start>` — preserved across collapse/expand. */
  start?: number;
  className?: string;
  children?: ReactNode;
  isStreaming?: boolean;
}

export function CollapsibleMarkdownList({
  ordered = false,
  start,
  className,
  children,
  isStreaming = false,
}: CollapsibleMarkdownListProps) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const items = useMemo(() => listItems(children), [children]);
  const collapsible = shouldCollapseList(items.length, isStreaming);
  // `collapsible` is false while streaming, so a list that grew past the threshold
  // mid-stream shows everything until the stream ends — then collapses.
  const shown = collapsible && !expanded ? items.slice(0, LIST_COLLAPSE_THRESHOLD) : items;

  // The id exists ONLY to be the toggle's aria-controls target. Emitting it on every
  // list would make the sub-threshold path differ from streamdown's own output for
  // no reason — and an id nothing references is an id someone later styles against.
  const id = collapsible ? listId : undefined;
  const list = ordered ? (
    <ol id={id} start={start} className={cn(OL_CLASS, className)} data-streamdown="ordered-list">
      {shown}
    </ol>
  ) : (
    <ul id={id} className={cn(UL_CLASS, className)} data-streamdown="unordered-list">
      {shown}
    </ul>
  );

  if (!collapsible) return list;

  return (
    <>
      {list}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={listId}
        className="mt-1 mb-2 cursor-pointer bg-transparent p-0 text-left text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        {expanded
          ? "Show less"
          : `Showing ${LIST_COLLAPSE_THRESHOLD} of ${items.length} — Show all`}
      </button>
    </>
  );
}
